import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../components/auth/AuthContext';

const DEFAULT_PAYROLL_VISIBILITY = {
  adminsCanSeeManagerPay:    false,
  adminsCanSeeOtherAdminPay: false,
  managersCanSeeAdminPay:    false,
};

const DEFAULT_SETTINGS = {
  managerCanApproveSchedules: false,
  managerCanManageTeam: false,
  roleNames: {
    manager: 'Manager',
    employee: 'Employee'
  },
  payrollVisibility: DEFAULT_PAYROLL_VISIBILITY,
};

const DEFAULT_FEATURES = {
  timeTracking:  true,
  scheduling:    false,
  payroll:       false,
  reporting:     false,
  teamManagement:true,
  ptoTracking:   false,
  clockInOut:    false,
  timeEntry:     true,
  mobileApp:     false,
  integrations:  false,
  evvCompliance: false,
};

export function useOrgSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async () => {
    const orgId = user?.organizationId;
    const token = user?.token;
    if (!orgId || !token) { setLoading(false); return; }

    try {
      const res = await fetch(`/api/organizations/${orgId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const org = await res.json();
        setSettings({
          managerCanApproveSchedules: org.settings?.managerCanApproveSchedules ?? false,
          managerCanManageTeam: org.settings?.managerCanManageTeam ?? false,
          roleNames: {
            manager: org.settings?.roleNames?.manager || 'Manager',
            employee: org.settings?.roleNames?.employee || 'Employee'
          },
          lunchSettings: org.lunchSettings || {},
          ptoPolicy: org.ptoPolicy || {},
          sickPolicy: org.sickPolicy || {},
          payrollVisibility: { ...DEFAULT_PAYROLL_VISIBILITY, ...(org.payrollVisibility || {}) },
        });
        setFeatures({ ...DEFAULT_FEATURES, ...(org.features || {}) });
      }
    } catch (err) {
      console.error('useOrgSettings: failed to fetch org settings', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId, user?.token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const roleLabel = (role) => {
    if (!role) return '';
    const r = role.toLowerCase();
    if (r === 'manager') return settings.roleNames.manager;
    if (r === 'employee') return settings.roleNames.employee;
    if (r === 'superadmin') return 'Super Admin';
    if (r === 'adminassistant') return 'Admin Assistant';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return { settings, features, roleLabel, loading, error, refetch: fetchSettings };
}